import { SectionCard } from '@/components/SectionCard/SectionCard'
import {
  type ResolveIngestError,
  toResolveIngestError,
} from '@/features/SalesforceIngest/api/resolveIngestHooks'
import { PayloadBlockSection } from '@/features/SalesforceIngest/components/PayloadBlockSection'
import { ReasonCodeGuidance } from '@/features/SalesforceIngest/components/ReasonCodeGuidance'
import { ReasonCodeSelect } from '@/features/SalesforceIngest/components/ReasonCodeSelect'
import type { AcceptedResolution } from '@/features/SalesforceIngest/components/ResolutionReceipt'
import { ResolveDecisionFields } from '@/features/SalesforceIngest/components/ResolveDecisionFields'
import { SubmitErrorAlert } from '@/features/SalesforceIngest/components/SubmitErrorAlert'
import { usePastedDecisions } from '@/features/SalesforceIngest/hooks/usePastedDecisions'
import { deliveryFailedAdvice } from '@/features/SalesforceIngest/lib/deliveryFailedAdvice'
import { describeResolution } from '@/features/SalesforceIngest/lib/describeResolution'
import { parsePayloadBlock } from '@/features/SalesforceIngest/lib/parsePayloadBlock'
import {
  NO_REASON_CODE,
  type ReasonCodeSelection,
  isAdvisory,
} from '@/features/SalesforceIngest/lib/reasonCodes'
import {
  type FieldErrors,
  buildResolveBody,
  describeIfComplete,
  validateResolveForm,
} from '@/features/SalesforceIngest/lib/resolveFormState'
import { useResolveSalesforceOpportunity } from '@/generated/http-clients/v3ApiComponents'
import { Button, Field, HStack, Textarea, VStack } from '@chakra-ui/react'
import { type FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onAccepted: (accepted: AcceptedResolution) => void
}

export function ResolveIngestForm({ onAccepted }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [reasonCode, setReasonCode] =
    useState<ReasonCodeSelection>(NO_REASON_CODE)
  const [rawPayload, setRawPayload] = useState('')
  const [reason, setReason] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<ResolveIngestError | null>(
    null,
  )

  const parsed = useMemo(() => parsePayloadBlock(rawPayload), [rawPayload])
  const {
    decision,
    isPrefilled,
    isPasteOpen,
    prefillKey,
    updateDecision,
    openPaste,
    notePastedBlock,
  } = usePastedDecisions(parsed, rawPayload)

  const { mutate, isPending } = useResolveSalesforceOpportunity()

  const trimmedDecision = { ...decision, orgId: decision.orgId.trim() }

  const deliveryAdvice =
    reasonCode === 'delivery-failed' && parsed.ok
      ? deliveryFailedAdvice(parsed.payload, rawPayload)
      : undefined

  const description = describeIfComplete(parsed, trimmedDecision)

  const dismissSubmitError = () => setSubmitError(null)

  const clearFieldError = (field: keyof FieldErrors) =>
    setFieldErrors((current) => ({ ...current, [field]: undefined }))

  const handleRawChange = (raw: string) => {
    setRawPayload(raw)
    clearFieldError('payload')
    dismissSubmitError()
  }

  const handleDecisionChange = (patch: Partial<typeof decision>) => {
    updateDecision(patch)
    clearFieldError('orgId')
    dismissSubmitError()
  }

  const handleReasonChange = (value: string) => {
    setReason(value)
    clearFieldError('reason')
    dismissSubmitError()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    dismissSubmitError()

    const errors = validateResolveForm(parsed, trimmedDecision, reason)
    setFieldErrors(errors)
    if (!parsed.ok || Object.keys(errors).length > 0) {
      return
    }

    mutate(
      { body: buildResolveBody(parsed.payload, trimmedDecision, reason) },
      {
        onSuccess: (response) => {
          onAccepted({
            ingestId: response.ingestId,
            opportunityId: parsed.payload.opportunityId,
            description: describeResolution(
              trimmedDecision,
              parsed.payload,
              parsed.licenceTotal,
            ),
          })
        },
        onError: (error) => setSubmitError(toResolveIngestError(error)),
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <SectionCard
        title={String(t('salesforceIngest.form.title'))}
        description={String(t('salesforceIngest.form.description'))}
      >
        <VStack align="stretch" gap={5}>
          <SubmitErrorAlert error={submitError} />

          <ReasonCodeSelect
            value={reasonCode}
            onChange={(code) => {
              setReasonCode(code)
              dismissSubmitError()
            }}
          />

          {isAdvisory(reasonCode) ? (
            <ReasonCodeGuidance
              code={reasonCode}
              deliveryAdvice={deliveryAdvice}
            />
          ) : null}

          <PayloadBlockSection
            raw={rawPayload}
            onRawChange={handleRawChange}
            parsed={parsed}
            error={fieldErrors.payload}
            isPasteOpen={isPasteOpen}
            onReplacePaste={openPaste}
            onBlockPasted={notePastedBlock}
          />

          <ResolveDecisionFields
            decision={decision}
            onChange={handleDecisionChange}
            isPrefilled={isPrefilled}
            prefillKey={prefillKey}
            orgIdError={fieldErrors.orgId}
            description={description}
          />

          <Field.Root required invalid={Boolean(fieldErrors.reason)}>
            <Field.Label>
              {String(t('salesforceIngest.form.reason.label'))}
              <Field.RequiredIndicator />
            </Field.Label>
            <Textarea
              value={reason}
              onChange={(event) => handleReasonChange(event.target.value)}
              placeholder={String(
                t('salesforceIngest.form.reason.placeholder'),
              )}
              rows={3}
            />
            {fieldErrors.reason ? (
              <Field.ErrorText>
                {String(t('salesforceIngest.form.reason.error.required'))}
              </Field.ErrorText>
            ) : (
              <Field.HelperText>
                {String(t('salesforceIngest.form.reason.helper'))}
              </Field.HelperText>
            )}
          </Field.Root>

          <HStack justify="flex-end">
            <Button type="submit" colorPalette="brand" loading={isPending}>
              {String(t('salesforceIngest.form.submit'))}
            </Button>
          </HStack>
        </VStack>
      </SectionCard>
    </form>
  )
}
